import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { listUsers, createUser, updateUser, toggleUserEnabled } from '../services/userService';
import { getAvatarEmoji } from '../utils/avatarCatalog';

const section = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

const INITIAL_FORM = { name: '', email: '', password: '', role: 'STAFF' };

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listUsers();
      setUsers(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  async function handleCreate(event) {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      await createUser(form);
      setForm(INITIAL_FORM);
      setShowForm(false);
      await loadUsers();
    } catch (err) {
      setError(err?.response?.data?.message || 'Error al crear el usuario');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveEdit(userId) {
    setSaving(true);
    try {
      await updateUser(userId, { name: editName });
      setEditingId(null);
      await loadUsers();
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(userId) {
    await toggleUserEnabled(userId);
    await loadUsers();
  }

  return (
    <motion.div variants={stagger} initial="hidden" animate="visible" className="space-y-6">
      {/* Header */}
      <motion.div variants={section} className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] p-8 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.32em] text-slate-400">Administración</p>
            <h1 className="mt-3 text-[54px] leading-[0.96] font-semibold tracking-[-0.06em] text-slate-950">Usuarios</h1>
            <p className="mt-4 text-lg text-slate-500">
              Gestiona las cuentas de empleados. Crea nuevos usuarios, edita nombres o desactiva accesos.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setShowForm(!showForm); setError(''); }}
            className="self-start rounded-[22px] bg-[#0a84ff] hover:bg-[#0077ed] text-white px-6 py-4 font-medium shadow-[0_14px_24px_rgba(10,132,255,0.22)] transition"
          >
            {showForm ? 'Cancelar' : '➕ Crear empleado'}
          </button>
        </div>
      </motion.div>

      {/* Create form */}
      <AnimatePresence>
        {showForm && (
          <motion.form
            key="create-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            onSubmit={handleCreate}
            className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] p-8 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl space-y-5 overflow-hidden"
          >
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">Nuevo usuario</p>
              <h2 className="mt-2 text-2xl font-medium text-slate-950">Crear cuenta de empleado</h2>
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nombre"
                required
                className="app-input"
              />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                required
                className="app-input"
              />
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Contraseña"
                required
                minLength={4}
                className="app-input"
              />
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="app-input"
              >
                <option value="STAFF">Empleado (STAFF)</option>
                <option value="ADMIN">Administrador (ADMIN)</option>
              </select>
            </div>
            {error && <p className="text-red-600 font-semibold">{error}</p>}
            <button
              type="submit"
              disabled={saving}
              className="rounded-[22px] bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-4 font-medium shadow-[0_14px_24px_rgba(16,185,129,0.22)] transition disabled:opacity-50"
            >
              {saving ? 'Creando...' : 'Crear usuario'}
            </button>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Users table */}
      <motion.div variants={section} className="rounded-[28px] border border-[var(--app-border)] bg-[var(--app-surface)] shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-[rgba(245,245,247,0.9)] text-slate-600">
              <tr>
                <th className="p-4 border-b border-[var(--app-border)] font-medium w-16"></th>
                <th className="p-4 border-b border-[var(--app-border)] font-medium">Nombre</th>
                <th className="p-4 border-b border-[var(--app-border)] font-medium">Email</th>
                <th className="p-4 border-b border-[var(--app-border)] font-medium">Rol</th>
                <th className="p-4 border-b border-[var(--app-border)] font-medium">Estado</th>
                <th className="p-4 border-b border-[var(--app-border)] font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-10 text-slate-500">
                    <div className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-slate-300 border-t-[var(--app-accent)] rounded-full animate-spin" />
                      Cargando...
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-slate-500">No hay usuarios</td>
                </tr>
              ) : (
                users.map((userItem) => (
                  <tr key={userItem.id} className="hover:bg-[rgba(245,245,247,0.7)] transition">
                    <td className="p-4 border-b border-[var(--app-border)] text-center text-2xl">
                      {getAvatarEmoji(userItem.avatarIndex)}
                    </td>
                    <td className="p-4 border-b border-[var(--app-border)]">
                      {editingId === userItem.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="app-input-compact rounded-xl"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => handleSaveEdit(userItem.id)}
                            disabled={saving}
                            className="rounded-xl bg-emerald-600 text-white px-3 py-2 text-sm font-medium"
                          >
                            ✓
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingId(null)}
                            className="rounded-xl bg-slate-200 text-slate-600 px-3 py-2 text-sm"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <span className="font-medium text-slate-900">{userItem.name}</span>
                      )}
                    </td>
                    <td className="p-4 border-b border-[var(--app-border)] text-slate-600">{userItem.email}</td>
                    <td className="p-4 border-b border-[var(--app-border)]">
                      <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                        userItem.role === 'ADMIN'
                          ? 'bg-violet-50 border border-violet-200 text-violet-600'
                          : 'bg-blue-50 border border-blue-200 text-blue-600'
                      }`}>
                        {userItem.role}
                      </span>
                    </td>
                    <td className="p-4 border-b border-[var(--app-border)]">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                        userItem.enabled
                          ? 'bg-emerald-50 border border-emerald-200 text-emerald-600'
                          : 'bg-red-50 border border-red-200 text-red-500'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${userItem.enabled ? 'bg-emerald-500' : 'bg-red-400'}`} />
                        {userItem.enabled ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="p-4 border-b border-[var(--app-border)]">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => { setEditingId(userItem.id); setEditName(userItem.name); }}
                          className="rounded-xl bg-[rgba(245,245,247,0.9)] border border-[var(--app-border)] px-3 py-2 text-sm font-medium text-slate-700 hover:bg-white transition"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggle(userItem.id)}
                          className={`rounded-xl px-3 py-2 text-sm font-medium transition ${
                            userItem.enabled
                              ? 'bg-red-50 border border-red-200 text-red-600 hover:bg-red-100'
                              : 'bg-emerald-50 border border-emerald-200 text-emerald-600 hover:bg-emerald-100'
                          }`}
                        >
                          {userItem.enabled ? 'Desactivar' : 'Activar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </motion.div>
  );
}
